<script lang="ts" setup>
import type { FolderPermissions } from '../types';
import type { Content, Scope } from '../../../runtime/services/StorageService';
import StorageService from '../../../runtime/services/StorageService';
import ResourcePermissions from '../../security/ResourcePermissions.vue';
import MetaModal from '../modals/MetaModal.vue';

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t, $i18n } = useLocalization();

const model = defineModel<FileResource>();
const props = defineProps<{
    scope: Scope;
    permissions: FolderPermissions;
}>();

const metaModalRef = ref<any>(null);

const resource = computed({
    get() { return model.value; },
    set(value) { model.value = value; }
});
const activeTab = ref<'details' | 'description' | 'permissions'>('details');
const changeTab = (name: 'details' | 'description' | 'permissions'): void =>
{
    activeTab.value = name;
};

const languages = computed<any[]>(() => $i18n.options());
const language = ref<string>(languages.value[0].value ?? null);
const updateLanguage = (): void =>
{
    language.value = languages.value?.[0]?.value ?? null;
};

const content = ref<Record<string, Content>>({});
const updateContent = (): void =>
{
    content.value = model.value.content || {} as Record<string, Content>;

    for (let i = 0; i < languages.value.length; i++)
    {
        const lang = languages.value[i].value;

        content.value[lang] = content.value[lang] || {} as Content;
    }
};

onBeforeMount(() =>
{
    updateLanguage();
    updateContent();
});

watch(() => resource.value, () =>
{
    imageError.value = false;
    updateLanguage();
    updateContent();
});

const loading = ref(false);
const saveContent = async (): Promise<void> =>
{
    try
    {
        loading.value = true;

        await StorageService.saveContent(resource.value.id, content.value);

        resource.value.content = content.value;
        $alert.success($t('[[[Zmiany zostały zapisane.]]]'));
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
    finally
    {
        loading.value = false;
    }
};

const imageError = ref(false);
</script>

<template>
    <div v-if="resource">
        <div class="d-flex justify-content-center mb-3">
            <img
                v-show="!imageError"
                v-if="resource.isImage"
                :src="$filters.image(resource.url, 'glance')"
                :alt="resource.name"
                class="rounded-3"
                style="max-width: 322px;"
                loading="lazy"
                @error="imageError = true"
            >
        </div>

        <ul class="nav nav-tabs nav-justified mb-3">
            <li class="nav-item">
                <a href="#" class="nav-link" :aria-current="activeTab === 'details'" :class="{ 'active': activeTab === 'details' }" @click.prevent="changeTab('details')">
                    {{ $t('[[[Właściwości]]]') }}
                </a>
            </li>
            <li v-if="resource.resourceType == 'file'" class="nav-item">
                <a href="#" class="nav-link" :aria-current="activeTab === 'description'" :class="{ 'active': activeTab === 'description' }" @click.prevent="changeTab('description')">
                    {{ $t('[[[Opisy]]]') }}
                </a>
            </li>
            <li v-if="resource.resourceType == 'folder' && props.scope != 'private' && props.permissions.canManage" class="nav-item">
                <a href="#" class="nav-link" :aria-current="activeTab === 'permissions'" :class="{ 'active': activeTab === 'permissions' }" @click.prevent="changeTab('permissions')">
                    {{ $t('[[[Uprawnienia]]]') }}
                </a>
            </li>
        </ul>

        <div v-show="activeTab === 'details'" class="details">
            <h5 class="d-flex align-items-center mb-3 pb-1">
                <i class="far fa-image me-3 text-info" />
                <b class="text-truncate" :title="resource.name">{{ resource.name }}</b>
            </h5>

            <h6>
                {{ $t('[[[Utworzył]]]') }}
            </h6>
            <ul class="list-unstyled">
                <li>
                    <i class="far fa-user" />
                    <span>{{ resource.createdBy }}</span>
                </li>
                <li>
                    <i class="far fa-clock" />
                    <span>{{ $filters.datetime(resource.dateCreatedUtc) }}</span>
                </li>
            </ul>

            <template v-if="$filters.datetime(resource.dateCreatedUtc) !== $filters.datetime(resource.dateModifiedUtc)">
                <h6>
                    {{ $t('[[[Zmodyfikował]]]') }}
                </h6>
                <ul class="list-unstyled">
                    <li>
                        <i class="far fa-user" />
                        <span>{{ resource.modifiedBy }}</span>
                    </li>
                    <li>
                        <i class="far fa-clock" />
                        <span>{{ $filters.datetime(resource.dateModifiedUtc) }}</span>
                    </li>
                </ul>
            </template>

            <template v-if="resource.resourceType === 'file'">
                <h6>
                    {{ $t('[[[Typ mime]]]') }}
                </h6>
                <ul class="list-unstyled">
                    <li>
                        <i class="far fa-user" />
                        <span>{{ resource.contentType }}</span>
                    </li>
                </ul>

                <h6>
                    {{ $t('[[[Rozmiar]]]') }}
                </h6>
                <ul class="list-unstyled">
                    <li>
                        <i class="far fa-clock" />
                        <span>{{ $filters.filesize(resource.contentLength) }}</span>
                    </li>
                </ul>

                <template v-if="resource.isImage">
                    <h6>
                        {{ $t('[[[Wymiary]]]') }}
                    </h6>
                    <ul class="list-unstyled">
                        <li>
                            <i class="far fa-clock" />
                            <span>{{ `${resource.meta.width}x${resource.meta.height} px` }}</span>
                        </li>
                    </ul>
                </template>
                <template v-if="resource.isImage">
                    <ActionButton
                        variant="light"
                        class="mb-2 w-100"
                        icon="far fa-info-circle"
                        :text="$t('[[[Dane exif]]]')"
                        @click.prevent="metaModalRef.showModal()"
                    />
                </template>
            </template>
        </div>
        <div v-show="activeTab === 'description'" class="mt-3 description">
            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Nazwa]]]')" label-for="resource-name">
                    <IdeoFormInput id="resource-name" :key="resource.id" v-model="content[shortLocale].name" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Opis]]]')" label-for="resource-description">
                    <textarea id="resource-description" :key="resource.id" v-model="content[shortLocale].lead" class="form-control" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Tekst alternatywny]]]')" label-for="resource-alt">
                    <IdeoFormInput id="resource-alt" :key="resource.id" v-model="content[shortLocale].alt" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Tagi]]]')" label-for="resource-tags">
                    <IdeoFormInput id="resource-tags" :key="resource.id" v-model="content[shortLocale].tags" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Autor]]]')" label-for="resource-author">
                    <IdeoFormInput id="resource-author" :key="resource.id" v-model="content[shortLocale].author" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Licencja]]]')" label-for="resource-licence">
                    <IdeoFormInput id="resource-licence" :key="resource.id" v-model="content[shortLocale].licence" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <IdeoFormLocalize v-slot="{ shortLocale }">
                <IdeoFormGroup :label="$t('[[[Źródło]]]')" label-for="resource-source">
                    <IdeoFormInput id="resource-source" :key="resource.id" v-model="content[shortLocale].source" type="text" :disabled="loading" />
                </IdeoFormGroup>
            </IdeoFormLocalize>

            <div class="d-flex justify-content-end">
                <FilemanagerResource
                    v-model="content"
                    :resource="resource"
                    :loading="loading"
                    @save="saveContent()"
                />
            </div>
        </div>
        <div v-if="activeTab === 'permissions'">
            <ResourcePermissions :id="resource.id" resource="Ideo.Core.Data.Model.StorageFolder" />
        </div>
    </div>
    <MetaModal :id="resource.id" ref="metaModalRef" v-model="resource.meta" />
</template>

<style lang="scss" scoped>
h6 {
    font-size: 0.875rem;
    margin-bottom: 0.625rem;
}
.details {
    .list-unstyled {
        margin-bottom: 1.25rem;

        li {
            &:not(:last-child) {
                margin-bottom: 0.625rem;
            }

            i {
                font-size: 1rem;
                min-width: 1rem;
                margin-right: 0.625rem;
            }

            span {
                font-size: 0.875rem;
            }
        }
    }
}
</style>
